import { cn, Flex, loadDomMax } from '@bridebook/aura';
import { IWedding_Website_Section } from '@bridebook/models/source/models/Weddings.types';
import { Divider } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/components/divider';
import { Palette } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/palette-config';
import { useIsPreviewWrapper } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/preview-wrapper-context';
import { useThemeContext } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/theme-provider';
import { SECTION_COMPONENTS } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/sections/section-components';
import { WeddingOverview } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/sections/wedding-overview/wedding-overview';
import { AnimatedSection } from '@bridebook/web/components/wedding-website/view-mode/partials/animated-section';
import { NON_REORDERABLE_SECTIONS } from '@bridebook/web/lib/wedding-website/constants';
import { DesktopPreviewDevice } from '@bridebook/web/lib/wedding-website/desktop/types';
import { TSectionName } from '@bridebook/web/lib/wedding-website/types';
import { isPrimaryPhotoSection } from '@bridebook/web/lib/wedding-website/utils/sections-typeguards';
import { useMeasure } from '@uidotdev/usehooks';
import { AnimatePresence, LayoutGroup, LazyMotion } from 'motion/react';
import React, { Fragment, memo, useMemo, useRef, useState } from 'react';
import { toMainPalette } from '../../config/utils';
import { useWeddingWebsiteData } from '../../data/data-context';
import { useActivePreviewDevice, useIsEditMode } from '../../data/data-provider-panel';
import { useScrollSyncCarousel } from '../hooks/use-scroll-sync-carousel';
import { EmptyPhotoPlaceholder } from './empty-photo-placeholder';
import { PhotoCarousel } from './photo-carousel';
import { SplitDesktopAccent } from './split-desktop-accent';

interface ContentSection {
  key: string;
  element: React.ReactNode;
}

/**
 * Builds content section entries from website.sections in the correct order.
 * - WeddingOverview is always first (covers non-reorderable sections)
 * - Reorderable sections follow in the order defined by website.sections
 */
const buildContentSections = (sections: IWedding_Website_Section[]): ContentSection[] => {
  const overviewSection: ContentSection = {
    key: 'overview',
    element: (
      <WeddingOverview>
        <SplitDesktopAccent />
      </WeddingOverview>
    ),
  };

  const reorderableSections = sections
    .filter((section) => {
      const sectionName = section.name as TSectionName;
      return !NON_REORDERABLE_SECTIONS.includes(sectionName);
    })
    .filter((section) => section.visible)
    .map((section): ContentSection | null => {
      const component = SECTION_COMPONENTS[section.name];
      if (!component) return null;
      return { key: section.name, element: component };
    })
    .filter((entry): entry is ContentSection => entry !== null);

  return [overviewSection, ...reorderableSections];
};

export const SplitLayoutWrapper = () => {
  const { website } = useWeddingWebsiteData();
  const isPreviewWrapper = useIsPreviewWrapper();
  const activePreviewDevice = useActivePreviewDevice();
  const isEditMode = useIsEditMode();
  const { palette } = useThemeContext();
  const mainPalette = toMainPalette(palette);
  const hasBridebookSwirls = mainPalette === Palette.Bridebook;

  // In preview mode, use activePreviewDevice to determine layout.
  // This bypasses container query issues when viewing mobile preview on desktop viewport.
  const isForcedMobilePreview =
    isPreviewWrapper && activePreviewDevice === DesktopPreviewDevice.Mobile;

  const contentRef = useRef<HTMLDivElement>(null);
  const [desktopCarouselRef, { width: desktopCarouselWidth }] = useMeasure();
  const isDesktopLayout = !isForcedMobilePreview && (desktopCarouselWidth ?? 0) > 0;

  const { validPhotos, visibleContentSections } = useMemo(() => {
    const photoSection = website.sections.find(isPrimaryPhotoSection);
    const photos = (photoSection?.data ?? []).filter((p) => p.path);

    // Build content sections dynamically based on the order in website.sections
    const sections = buildContentSections(website.sections);

    return { validPhotos: photos, visibleContentSections: sections };
  }, [website.sections]);

  const hasPhotos = validPhotos.length > 0;

  const { activeIndex } = useScrollSyncCarousel({
    photoCount: validPhotos.length,
    contentRef,
    enabled: isDesktopLayout && validPhotos.length > 1,
    targetContainerScroll: isPreviewWrapper,
  });

  const [mobileCarouselIndex, setMobileCarouselIndex] = useState(0);
  const prevPhotoCountRef = useRef(validPhotos.length);
  if (prevPhotoCountRef.current !== validPhotos.length) {
    prevPhotoCountRef.current = validPhotos.length;
    if (mobileCarouselIndex !== 0) {
      setMobileCarouselIndex(0);
    }
  }

  return (
    <Flex className={cn('w-full flex-col', !isForcedMobilePreview && '@screen-lg:flex-row')}>
      <div
        ref={desktopCarouselRef}
        className={cn(
          isForcedMobilePreview
            ? 'hidden'
            : 'hidden @screen-lg:sticky @screen-lg:top-0 @screen-lg:block @screen-lg:h-[var(--desktop-height,100vh)] @screen-lg:w-[60%]',
        )}>
        {hasPhotos ? (
          <PhotoCarousel photos={validPhotos} activeIndex={activeIndex} variant="desktop" />
        ) : (
          <EmptyPhotoPlaceholder />
        )}
      </div>

      {hasPhotos && (
        <div className={cn('relative z-10 w-full', !isForcedMobilePreview && '@screen-lg:hidden')}>
          <PhotoCarousel
            photos={validPhotos}
            activeIndex={mobileCarouselIndex}
            onIndexChange={setMobileCarouselIndex}
            variant="mobile"
            autoplay
            animation="slide"
          />
        </div>
      )}

      <Flex
        ref={contentRef}
        className={cn(
          'relative w-full flex-col items-center overflow-hidden bg-[var(--ww-palette-surface-secondary,#fff)]',
          !isForcedMobilePreview && '@screen-lg:w-[40%]',
          // For Bridebook theme, background is provided by SplitContentPanelBackground at root level
          // to allow swirls to appear between background and content
          !isForcedMobilePreview &&
            (hasBridebookSwirls
              ? '@screen-lg:z-[2] @screen-lg:bg-transparent'
              : '@screen-lg:bg-[var(--ww-palette-surface-secondary)]'),
          !isForcedMobilePreview &&
            (isPreviewWrapper
              ? '@screen-lg:h-[var(--desktop-height,100vh)] @screen-lg:overflow-y-auto'
              : '@screen-lg:min-h-[var(--desktop-height,100vh)]'),
        )}>
        <ContentSections sections={visibleContentSections} isEditMode={isEditMode} />
      </Flex>
    </Flex>
  );
};

const ContentSections = memo(function ContentSections({
  sections,
  isEditMode,
}: {
  sections: ContentSection[];
  isEditMode: boolean;
}) {
  // In edit mode, use layout animations for smooth reordering
  if (isEditMode) {
    return (
      <LazyMotion features={loadDomMax}>
        <LayoutGroup>
          <AnimatePresence mode="popLayout">
            {sections.map((section, index) => (
              <AnimatedSection key={section.key} layoutId={section.key}>
                {index > 0 && <Divider />}
                {section.element}
              </AnimatedSection>
            ))}
          </AnimatePresence>
        </LayoutGroup>
      </LazyMotion>
    );
  }

  // In guest view, render without motion for better performance
  return (
    <>
      {sections.map((section, index) => (
        <Fragment key={section.key}>
          {index > 0 && <Divider />}
          {section.element}
        </Fragment>
      ))}
    </>
  );
});
